import { useMemo, useEffect, type ReactNode } from "react";
import classNames from "classnames";
import { useParams, useLocation } from "react-router-dom";
import { CLASSIC_PATCH_OPTION } from "@ugg/shared/query-params/valid-params";
import { ChampionProfilePageNav } from "@ugg/shared/components/Champions/ChampionProfilePageNav";
import { useUGGPatches } from "@ugg/shared/api/requests/patches";
import { asArray } from "@ugg/shared/utils/safe-coerce";
import { convertPatchDisplay } from "@ugg/shared/utils/patch-helpers";
import { getRoleLabel, isSRMainRole, normalizeRole, RoleS } from "@ugg/shared/utils/role-helpers";
import { normalizeChampionUrlAlias } from "@ugg/shared/utils/champion-alias";
import { Error404 } from "@ugg/shared/components/common/Error404";
import { ALL_TIERS, type TierNameValue } from "@ugg/shared/utils/tier-helpers";
import { useClassicChampionSEO } from "@ugg/shared/utils/seo-helpers";
import {
  useClassicChampions,
  useClassicChampionJSON,
  useClassicChampionSkillsMini,
  useClassicMasteryDisplay,
  useClassicChampionMastery,
  useClassicChampionTierList,
  getClassicRoleLetter,
  getClassicRoleFromLetter,
  useClassicSpells,
  useClassicSpellBuild,
  useClassicPerks,
  useClassicRuneBuild,
  useClassicItems,
  useClassicItemsMini,
  useClassicItemBuild,
  resolveClassicAssetVersion,
  type ClassicChampionData,
  type ClassicSummonerSpell,
  type ClassicPerk,
  type ClassicItem,
} from "@ugg/shared/hooks/classic";
import { ClassicHeader } from "./ClassicHeader";
import { ClassicFilterBar } from "./ClassicFilterBar";
import { ClassicRecommendedPanel } from "./ClassicRecommendedPanel";
import { ClassicMasteries } from "./ClassicMasteries";
import { ClassicSkillsDetail } from "./ClassicSkillsDetail";

// ═══════════════════════════════════════════════════════════════════════════
// League Classic champion profile ("Classic Build" page).
//
// This folder is intentionally self-contained. League Classic is not
// data-driven the way the normal champion pages are — its content comes from a
// separate hand-crafted cdragon pipeline (see @ugg/shared/hooks/classic) and
// the modules themselves ("Masteries", "Skill Tooltips") have no modern
// equivalent to reuse.
//
// FOLDER BOUNDARY — this used to be one ~2000-line file, on the reasoning that
// keeping every Classic-only piece in one place meant we could swap it out
// wholesale if Classic ever rejoins the standard riot-assets build pages. That
// property is about blast radius, not file count, so it survives the split
// intact under two rules:
//
//   1. Outside code imports from ./index only. Nothing reaches into a part file.
//   2. Nothing in here gets hoisted into components/common, ChampionProfileHeader
//      or any other shared directory. Shared primitives are imported IN, never
//      exported OUT.
//
// Follow both and `rm -rf` on this directory still removes the feature cleanly.
//
// The parts:
//   ClassicHeader            portrait, tier badge, ability row
//   ClassicFilterBar         role links + queue/patch/region selects
//   ClassicRecommendedPanel  runes, spells, skill priority, items
//   ClassicMasteries         the three mastery trees
//   ClassicSkillsDetail      per-ability detail cards
//   ClassicItemPrimitives    item icon/tooltip pieces used by the panel
//   lib/skill-tooltip        dynamicDescription rendering + shared skill bits
//
// Data hooks live in @ugg/shared/hooks/classic (shared with the tier list); a
// handful of genuinely cross-cutting hookups (SEO entry, the shared page nav)
// live in their normal homes because they can't be localized here.
//
// Layout goal: read as close to the real Build page as possible so navigating
// between a champion's normal build and Classic build is not jarring. We reuse
// the legacy build SCSS class names (`champion-recommended-build`,
// `content-section*`, `toughest-matchups`, `recommended-build_*`,
// `skill-path-container`, …) so the modules inherit the same styling, and we
// reuse a few presentational shared components verbatim (Plus CTAs, promos).
//
// Much of the build content is still placeholder: runes, summoner spells and
// items render empty slots at the correct size to be filled in once we design
// the custom modules and wire their JSON. Masteries / Skills / Skill Tooltips
// render from real fetched data; where a curated build feed doesn't exist for a
// champion yet the module renders its empty state.
//
// A set of alternate module presentations (Toughest Matchups, a standalone
// Items block, Champion Info, and Rune/Mastery/Items summaries) shipped behind
// `{false && …}` in the original Classic commit and were never enabled. They've
// been removed; recover them from a07ccc8b1 if a product need comes back.
// ═══════════════════════════════════════════════════════════════════════════

// Role query-param default (drives labels + matchup links) when the champion
// has no tier-list entry to derive a core role from.
const DEFAULT_ROLE = "mid";

// ═══════════════════════════════════════════════════════════════════════════
// Main component
// ═══════════════════════════════════════════════════════════════════════════

export function ClassicChampionProfile({
  className,
  bottomAd,
  autoImportButton,
}: {
  // Shell-supplied layout override for the page root. The default width
  // below is the website's content column; the desktop app runs a
  // narrower one (--main-col-width) and needs to override it.
  className?: string;
  bottomAd?: ReactNode;
  // Injected from the web shell so we can reuse the real AppDownloadButton
  // (which lives in the web layer) for exact style/size parity. The app
  // shell leaves this empty — Classic's old runes/masteries can't be
  // expressed through the LCU perk/item-set APIs.
  autoImportButton?: ReactNode;
}) {
  // On the classic route (/lol/champions/classic/:champion-classic/:role?) the
  // `-classic` suffix is literal, so `champion` resolves to the bare alias
  // ("ahri"); `role` is the optional trailing path segment (SEO-friendly, in
  // place of the old ?role= query param).
  const { champion: aliasParam, role: roleParam } = useParams<{ champion: string; role?: string }>();

  // Normalized so the one riot id whose URL alias differs ("monkeyking" →
  // "wukong") resolves against the manifest, which is keyed on the canonical
  // alias only. Done once here rather than at the lookup so everything derived
  // from it agrees — the SEO canonical, the role links in the filter bar, and
  // the scroll-reset key all take the canonical spelling.
  const alias = normalizeChampionUrlAlias(aliasParam);

  const { data: uggPatches } = useUGGPatches({ ssr: true });
  const location = useLocation();

  // Patch filter — the real, working version selector (was a mocked single-
  // option select). The window mirrors the Classic tier list exactly: the
  // newest `CLASSIC_PATCH_OPTION.max` u.gg patches. Options are derived from the
  // SSR-fetched `uggPatches` (rather than usePatchOptions' own non-SSR fetch) so
  // they're present on the server render too, matching usePatchOptions' output
  // (same slice + same "26.14" label). The selection lives in the `?patch=`
  // query param; an absent/invalid value falls back to latest, so the canonical
  // (latest) URL carries no patch param.
  //
  // Resolved FIRST, ahead of every data hook, because the Classic asset CDN is
  // version-scoped now — the champion manifest, catalogs and every icon URL are
  // built from it, so nothing can fetch until the patch is known.
  const patchOptions = useMemo(
    () =>
      (uggPatches || [])
        .slice(0, CLASSIC_PATCH_OPTION.max)
        .map((el) => ({ value: el, label: convertPatchDisplay(el).replace("_", ".") })),
    [uggPatches],
  );
  const latestPatch = uggPatches?.[0];
  const queryPatch = new URLSearchParams(location.search).get("patch") || undefined;
  const patch = queryPatch && patchOptions.some((o) => o.value === queryPatch) ? queryPatch : latestPatch;
  const patchDisplay = convertPatchDisplay(patch || "").replace("_", ".");

  // The <version> path segment for asset URLs (portraits, ability/rune/spell/
  // item/mastery icons). Clamped to the oldest published asset folder, so the
  // still-selectable 26.14 keeps rendering icons — see
  // CLASSIC_ASSET_VERSION_FLOOR. Undefined until the patch resolves; the
  // modules below skip their <img> tags rather than request a broken URL.
  const assetVersion = resolveClassicAssetVersion(patch);

  const { data: manifest, loading: manifestLoading, error: manifestError } = useClassicChampions({ patch, ssr: true });
  const { championKey, displayName } = useMemo(() => {
    const match = Object.entries(manifest || {}).find(([, entry]) => entry?.[0] === alias);
    return { championKey: match?.[0], displayName: match?.[1]?.[1] };
  }, [manifest, alias]);

  const { data: champion } = useClassicChampionJSON<ClassicChampionData>({
    championKey: championKey || "",
    patch,
    ssr: true,
  });
  const { data: masteryDisplay } = useClassicMasteryDisplay({ patch, ssr: true });

  // Curated skill priority — the compact "mini" feed (a flat ["Q","W","E"]
  // array keyed by champion). Only some champions have data yet; a missing file
  // → undefined → blank placeholder slots. (The full useClassicChampionSkills
  // feed is retained in the hooks module for when we resurface the leveling path.)
  const { data: skillsMini } = useClassicChampionSkillsMini({
    championKey: championKey || "",
    patch,
    ssr: true,
  });

  // Single curated mastery tree, keyed by champion. Most champions have no file
  // yet → empty selection, so the tree renders with no allocations (no fallback).
  const { selected: masterySelected } = useClassicChampionMastery({ championKey, patch, ssr: true });

  // Summoner spells: the champion-specific recommended pairing (keyed by champion
  // key, like skills/masteries/items) + the manifest that resolves ids →
  // names/tooltips. Champions without a build file yet → no data (empty module).
  const { data: spellBuild } = useClassicSpellBuild({ name: championKey || "", patch, ssr: true });
  const { data: spellManifest } = useClassicSpells({ patch, ssr: true });
  const spellsById = useMemo(() => {
    const map: Record<number, ClassicSummonerSpell> = {};
    asArray(spellManifest).forEach((s) => (map[s.id] = s));
    return map;
  }, [spellManifest]);

  // Runes: the champion-specific perk build (keyed by champion key, like the
  // other feeds) + the perk manifest that resolves ids → title/effect/icon.
  // Champions without a build file yet → no data (empty module).
  const { data: runeBuild } = useClassicRuneBuild({ name: championKey || "", patch, ssr: true });
  const { data: perkManifest } = useClassicPerks({ patch, ssr: true });
  const perksById = useMemo(() => {
    const map: Record<number, ClassicPerk> = {};
    asArray(perkManifest).forEach((p) => (map[p.id] = p));
    return map;
  }, [perkManifest]);

  // Items: the champion-specific build (keyed by champion key, like skills and
  // masteries), the mini id→name manifest for fast icon/name render, and the
  // full manifest (loaded async) for tooltips. Champions without a build file
  // yet resolve to no data → the module renders its empty placeholders.
  const { data: itemBuild } = useClassicItemBuild({ name: championKey || "", patch, ssr: true });
  const { data: itemsMini } = useClassicItemsMini({ patch, ssr: true });
  const { data: itemsFull } = useClassicItems({ patch });
  const itemsById = useMemo(() => {
    const map: Record<number, ClassicItem> = {};
    asArray(itemsFull).forEach((i) => (map[i.id] = i));
    return map;
  }, [itemsFull]);

  const getClassicSEO = useClassicChampionSEO();

  // Classic tier list (single per-patch feed, shared with the tier-list page).
  // Used to derive: (1) the champion's "core role" — highest-tier role, ties
  // broken by first-listed — which seeds the default role selection, (2) the
  // set of roles Classic actually has data for, which is what the role filter
  // offers, and (3) the per-role tier used on the portrait + in SEO copy. Keyed
  // to the selected patch so the tier data follows the version filter (the hook
  // validates `patch` against the Classic params and falls back to latest).
  const { data: tierListData } = useClassicChampionTierList({ params: { patch }, ssr: true });
  const { coreRole, coreTier, roleTierByLetter, availableRoles } = useMemo(() => {
    const tierlist = tierListData?.tierlist;
    const keyNum = Number(championKey);
    const byLetter: Partial<Record<string, TierNameValue>> = {};
    let core: { letter?: string; tier?: TierNameValue } = {};
    if (tierlist && championKey) {
      // ALL_TIERS is ordered best → worst, so the first entry we hit for this
      // champion is its best tier; the first such entry overall is the core role.
      for (const tier of ALL_TIERS) {
        // asArray + Array.isArray guard: a malformed tier bucket (non-array) or a
        // non-pair entry must not throw here — this runs in render, so a throw
        // would crash the whole page. Number() compare so a string-keyed feed
        // ("60001") still matches instead of silently finding no core role.
        for (const entry of asArray(tierlist[tier])) {
          if (!Array.isArray(entry) || Number(entry[0]) !== keyNum) continue;
          const up = String(entry[1]).toUpperCase();
          if (!byLetter[up]) byLetter[up] = tier;
          if (!core.letter) core = { letter: up, tier };
        }
      }
    }
    // Derived from the same tier entries rather than hardcoded to one role:
    // today the feed lists every champion under exactly one role, but if a
    // champion ever gets a second the filter grows a second icon on its own.
    const roles = Object.keys(byLetter)
      .map((letter) => getClassicRoleFromLetter(letter))
      .filter((r): r is RoleS => !!r);
    return {
      coreRole: getClassicRoleFromLetter(core.letter),
      coreTier: core.tier,
      roleTierByLetter: byLetter,
      availableRoles: roles,
    };
  }, [tierListData, championKey]);

  // ROLE SELECTION — Classic only has build data for a champion's tier-list
  // role, so that role is the page, whatever the URL says.
  //
  // A role in the path is honored only when Classic actually has data for it;
  // otherwise it's dropped and the core role renders. That keeps every legacy
  // /…-classic/<role> link working (it lands on the real build instead of
  // mislabeling it) and makes any *producer* of those URLs safe without
  // auditing them — getClassicChampionBuildUrl validates the role slug but has
  // no way to know which roles have data, so callers like TierList's
  // ChampionLink can still pass one through.
  //
  // SEO gets a stricter rule than rendering. The helper appends the role to the
  // canonical URL, and a role segment only earns its own canonical when it
  // distinguishes anything — with a single available role, /…-classic/mid and
  // /…-classic are the same page, so both canonicalize to the bare URL every
  // other link on the site points at. Only once a champion has two or more
  // roles does each role URL become a page in its own right. Without this the
  // duplicate-content problem narrows from five URLs to two instead of closing.
  const roleInPath = roleParam && isSRMainRole(roleParam) ? (normalizeRole(roleParam) as string) : undefined;
  const supportedRoleInPath = roleInPath && availableRoles.includes(roleInPath as RoleS) ? roleInPath : undefined;
  const canonicalRoleInPath = availableRoles.length > 1 ? supportedRoleInPath : undefined;
  const role = supportedRoleInPath ?? coreRole ?? DEFAULT_ROLE;
  const roleLabel = getRoleLabel(role, (label) => (label === "Bottom" ? "ADC" : label));
  const name = displayName ?? alias;

  // Tier to show for the current role. Now that `role` can only be a role the
  // champion is actually listed under, the first branch hits whenever the tier
  // list has resolved; `coreTier` covers the window before it does.
  const roleLetter = getClassicRoleLetter(role);
  const displayTier = (roleLetter && roleTierByLetter[roleLetter]) || coreTier;

  // Scroll to top when the champion changes, not just on first mount: navigating
  // between two Classic champions reuses this same route (`.../classic/:champion`),
  // so React Router keeps this component mounted. Keyed on `alias` (the champion),
  // NOT the full location, so switching the role filter on the same champion keeps
  // the user's scroll position.
  useEffect(() => {
    window && window.scrollTo(0, 0);
  }, [alias]);

  // The route matches any `<alias>-classic`, so an alias with no Classic
  // counterpart ("gwen-classic") or no champion at all ("notachampion-classic")
  // lands here too. Without this the page still rendered: 200, full chrome, and
  // an empty <head> — getClassicSEO emits nothing without a championName — which
  // is a soft 404. That's ~110 champions plus arbitrary strings, all crawlable
  // and titleless. Render the site's standard Not Found instead, same as the
  // modern build page does for an unknown champion.
  //
  // Deliberately placed after every hook (React can't have a conditional return
  // above them) and gated on the manifest having actually RESOLVED:
  //   • still loading, or skipped because `patch` isn't known yet → `manifest` is
  //     undefined, so we fall through and render the normal loading state.
  //   • fetch errored → render the page degraded rather than tell a crawler the
  //     URL doesn't exist, which is not recoverable once indexed.
  // Only a loaded manifest that genuinely lacks the alias produces a 404.
  if (manifest && !manifestLoading && !manifestError && !championKey) {
    return <Error404 />;
  }

  return (
    <div className={classNames("relative z-[1] w-[1014px] max-md:w-full mx-auto pt-[38px] max-sm:pt-[24px]", className)}>
      {getClassicSEO({ championName: displayName, alias, patch, roleInPath: canonicalRoleInPath })}

      <ClassicHeader
        championKey={championKey}
        name={name}
        champion={champion}
        roleLabel={roleLabel}
        patch={patchDisplay}
        assetVersion={assetVersion}
        tier={displayTier}
      />

      {/* Same nav as the normal champion page: "Classic" is highlighted here
          (its activePaths match this route), "Build" links back to the normal
          build, and every other tab targets the bare champion alias. */}
      <ChampionProfilePageNav />

      <ClassicFilterBar
        className="my-[24px]"
        role={role}
        availableRoles={availableRoles}
        alias={alias}
        patchOptions={patchOptions}
        selectedPatch={patch || ""}
        latestPatch={latestPatch}
      />

      {/* `champion-recommended-build` is a legacy build-page SCSS hook, and
          the two shells define it differently — flex/max-w-1014 on the
          website, grid/width-976 in the desktop app. The explicit `!flex`
          and `!w-full` keep the layout identical in both. */}
      <div className="champion-recommended-build !flex !w-full flex-col gap-[12px]">
        {/* Stats bar removed (reclaiming top space) — the Recommended panel is
            now the first module; the 12px flex gap separates the modules below. */}
        <ClassicRecommendedPanel
          name={name}
          roleLabel={roleLabel}
          assetVersion={assetVersion}
          spellIds={asArray(spellBuild?.spells)}
          spellsById={spellsById}
          runeBuild={runeBuild}
          perksById={perksById}
          autoImportButton={autoImportButton}
          championKey={championKey}
          champion={champion}
          skillsMini={skillsMini}
          itemBuild={itemBuild}
          itemsById={itemsById}
          itemsMini={itemsMini}
        />
        <ClassicMasteries
          masteryDisplay={masteryDisplay}
          selected={masterySelected}
          name={name}
          roleLabel={roleLabel}
          assetVersion={assetVersion}
        />
        <ClassicSkillsDetail championKey={championKey} champion={champion} assetVersion={assetVersion} />
        {bottomAd}
      </div>
    </div>
  );
}

// ─── Header ──────────────────────────────────────────────────────────────────
